/**
 * Staff Selection Page
 * Configure staff selection options for online booking. When the salon picks the technician
 * (Preferences), clients never see a technician list or an Anyone choice, so the shuffle, Anyone
 * and free-up toggles have no effect and are hidden; who gets the bookings still applies.
 */

import { createFileRoute, useParams } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import { SettingsPageHeader } from '@/components/ui'
import { BusinessSetupPageShell } from './business-setup/components/business-setup-page-shell'
import {
  RandomizeStaffOrderCard,
  ShowAnyoneOptionCard,
} from './online-booking/components/staff-selection-options-card'
import { AnyoneStaffAssignmentsCard } from './online-booking/components/anyone-staff-assignments-card'
import { FreeUpRequestedTechniciansCard } from './online-booking/components/free-up-requested-technicians-card'
import { formatDocumentTitle } from '@/lib/document-title'
import { useOnlineBookingSettings } from './online-booking/hooks/use-online-booking-settings'
import { isSalonPicksTechnician } from './online-booking/constants'

export const Route = createFileRoute('/_auth/$orgId/settings/online-booking/staff-selection')({
  component: StaffSelectionPage,
})

function StaffSelectionPage() {
  const { orgId } = useParams({ from: '/_auth/$orgId/settings/online-booking/staff-selection' })
  const { data: settingsResponse, isLoading } = useOnlineBookingSettings()
  const salonPicks = isSalonPicksTechnician(settingsResponse?.data)
  // Cards that only apply while clients choose wait for the settings, so they never flash in and vanish.
  const showClientChoiceCards = !isLoading && !salonPicks

  // The page opens only with Calendar on (route guard), where Hayven online booking can always be on.
  const settingsCards = (
    <>
      {showClientChoiceCards && <RandomizeStaffOrderCard />}
      {showClientChoiceCards && <ShowAnyoneOptionCard />}
      <AnyoneStaffAssignmentsCard />
      {showClientChoiceCards && <FreeUpRequestedTechniciansCard />}
    </>
  )

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Staff Selection', 'Online Booking')}</title>
      </Helmet>

      <BusinessSetupPageShell orgId={orgId} activePath="staff-selection">
        <SettingsPageHeader
          title="Staff Selection"
          description={
            salonPicks
              ? 'Choose who gets your online bookings.'
              : 'Choose how technicians appear and how "Anyone" clients are shared.'
          }
        />

        <div className="space-y-6 max-w-2xl">
          {settingsCards}
        </div>
      </BusinessSetupPageShell>
    </>
  )
}
